import React, { useState, useEffect, useMemo, useRef } from 'react';
import { FaStickyNote, FaSearch, FaPlus, FaTrash, FaEdit, FaCheckDouble, FaEllipsisV, FaChevronDown, FaIndustry, FaMapMarkerAlt } from 'react-icons/fa';
import { RiDeleteBin5Fill } from 'react-icons/ri';
import { useParams, useNavigate } from 'react-router-dom';
import { SiNotion } from "react-icons/si";
import Swal from 'sweetalert2';
import axios from 'axios';
import { IoMdArrowDropdown } from "react-icons/io";
import './Notes.css';

// Last loaded data of each shop is kept, so the notes show instantly
// on the next visit while fresh data loads in the background.
const NOTES_CACHE_PREFIX = 'notes_cache_';
const NOTES_CITIES_PREFIX = 'notes_cities_';

const readJson = (key) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

const writeJson = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (e) {}
};

const Notes = () => {
  const { shopId } = useParams();
  const navigate = useNavigate();

  // The shop is known from the very first render (from the URL), and the saved data
  // of that shop is shown instantly while fresh data loads in the background.
  const initialRef = useRef(null);
  if (initialRef.current === null) {
    const cached = shopId ? readJson(`${NOTES_CACHE_PREFIX}${shopId}`) : null;
    const cachedCities = shopId ? readJson(`${NOTES_CITIES_PREFIX}${shopId}`) : null;
    initialRef.current = {
      cache: cached && Array.isArray(cached.list) ? cached : null,
      cities: Array.isArray(cachedCities) ? cachedCities : null
    };
  }

  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedManufacturingId, setSelectedManufacturingId] = useState('');
  const [noteContent, setNoteContent] = useState('');
  const [selectedNotes, setSelectedNotes] = useState([]);
  const [showActionDropdown, setShowActionDropdown] = useState(false);
  const [showManufacturingDropdown, setShowManufacturingDropdown] = useState(false);
  const [manufacturingSearch, setManufacturingSearch] = useState('');
  const [manufacturingDataList, setManufacturingDataList] = useState(
    initialRef.current.cache ? initialRef.current.cache.list : []
  );
  // True only while there is nothing to show yet (so "No notes found" never flashes)
  const [isLoading, setIsLoading] = useState(!initialRef.current.cache);
  const [editingId, setEditingId] = useState(null);
  const [saveLoading, setSaveLoading] = useState(false);
  const [shops, setShops] = useState([]);
  const [selectedShopId, setSelectedShopId] = useState('');
  const [currentShopName, setCurrentShopName] = useState(
    initialRef.current.cache ? (initialRef.current.cache.shopName || '') : ''
  );

  // ==========================================
  // CITY DROPDOWN STATES
  // ==========================================
  const [shopCities, setShopCities] = useState(initialRef.current.cities || []);
  const [selectedCity, setSelectedCity] = useState('');
  // true once the cities of the shop are known (so "No cities available" never flashes)
  const [citiesReady, setCitiesReady] = useState(!!initialRef.current.cities);

  // ==========================================
  // HEADER CITY DROPDOWN (CUSTOM)
  // ==========================================
  const [headerCities, setHeaderCities] = useState(initialRef.current.cities || []);
  const [headerCity, setHeaderCity] = useState('');
  const [showHeaderCityDropdown, setShowHeaderCityDropdown] = useState(false);

  const dataRequestRef = useRef(0);
  const citiesRequestRef = useRef(0);
  const shopsFetchedRef = useRef(false);
  const prevShopRef = useRef(shopId || null);
  const currentShopNameRef = useRef(currentShopName);
  currentShopNameRef.current = currentShopName;

  // API Base URL
  const API_BASE_URL = 'https://super-backend.hira-tech.co.uk/inventory';

  // SweetAlert helpers
  const showSuccessAlert = (message) => {
    Swal.fire({
      icon: 'success',
      title: 'Success!',
      text: message,
      timer: 2000,
      showConfirmButton: true,
      confirmButtonColor: '#004aad'
    });
  };

  const showErrorAlert = (message) => {
    Swal.fire({
      icon: 'error',
      title: 'Error!',
      text: message,
      confirmButtonColor: '#d33'
    });
  };

  const showWarningAlert = (message) => {
    Swal.fire({
      icon: 'warning',
      title: 'Warning!',
      text: message,
      confirmButtonColor: '#f0ad4e'
    });
  };

  const showDeleteConfirmation = (noteContent, isMultiple = false, count = 1) => {
    const title = isMultiple
      ? `Are you sure you want to delete ${count} note(s)?`
      : `Are you sure you want to delete this note?`;

    return Swal.fire({
      title: 'Are you sure?',
      text: title,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    });
  };

  // ==========================================
  // FETCH CITIES FOR SELECTED SHOP (modal refresh, runs in the background)
  // ==========================================
  const fetchCitiesForShop = async (shopIdParam) => {
    if (!shopIdParam) {
      setShopCities([]);
      setSelectedCity('');
      return;
    }

    try {
      const response = await axios.get(`${API_BASE_URL}/shop-cities/?shop_id=${shopIdParam}`);
      if (response.data && response.data.cities) {
        setShopCities(response.data.cities);
        setCitiesReady(true);
        if (response.data.cities.length === 1) {
          setSelectedCity(response.data.cities[0]);
        }
      } else {
        setShopCities([]);
        setCitiesReady(true);
      }
    } catch (error) {
      console.error('Error fetching cities:', error);
    }
  };

  const fetchShops = async () => {
    try {
      const response = await axios.get(`https://super-backend.hira-tech.co.uk/api/product/shops/`);
      if (response.data) {
        const shopsData = Array.isArray(response.data) ? response.data : [];
        setShops(shopsData);

        if (!shopId && shopsData.length > 0) {
          const firstShop = shopsData[0];
          navigate(`/shop/${firstShop.shop_id}/Notes`, { replace: true });
        } else if (!shopId) {
          setIsLoading(false);
        }
      }
    } catch (error) {
      console.error('Error fetching shops:', error);
      if (!shopId) setIsLoading(false);
    }
  };

  // ==========================================
  // FETCH DATA: ONE request gives the manufacturing items AND their notes
  // (runs in the background: only the latest request updates the screen)
  // ==========================================
  const fetchData = async () => {
    if (!shopId) return;
    const id = shopId;
    const requestId = ++dataRequestRef.current;

    try {
      const response = await axios.get(`${API_BASE_URL}/api/manufacturing/?shop_id=${id}`);
      if (requestId !== dataRequestRef.current) return;

      if (response.data) {
        const list = Array.isArray(response.data) ? response.data : [];
        setManufacturingDataList(list);
        setIsLoading(false);

        let name = '';
        if (list.length > 0 && list[0].shop) {
          name = list[0].shop.shop_name;
        }
        writeJson(`${NOTES_CACHE_PREFIX}${id}`, { list, shopName: name || currentShopNameRef.current });

        if (name) {
          setCurrentShopName(name);
        } else {
          try {
            const shopResponse = await axios.get(`https://super-backend.hira-tech.co.uk/api/product/shops/${id}/`);
            if (shopResponse.data) name = shopResponse.data.shop_name;
          } catch (err) {
            console.error('Error fetching shop details:', err);
            name = `Shop ${id}`;
          }
          if (requestId !== dataRequestRef.current) return;
          if (name) {
            setCurrentShopName(name);
            writeJson(`${NOTES_CACHE_PREFIX}${id}`, { list, shopName: name });
          }
        }
      }
    } catch (err) {
      if (requestId !== dataRequestRef.current) return;
      console.error('Error fetching manufacturing data:', err);
      if (err.response?.status === 404) {
        showErrorAlert('Manufacturing API not available. Please check backend configuration.');
      } else {
        showErrorAlert('Failed to load manufacturing data');
      }
    } finally {
      if (requestId === dataRequestRef.current) setIsLoading(false);
    }
  };

  // The notes are taken from the loaded manufacturing items
  const notes = useMemo(() => {
    const allNotes = [];
    manufacturingDataList.forEach(manufacturingItem => {
      if (manufacturingItem.notes && manufacturingItem.notes.length > 0) {
        manufacturingItem.notes.forEach(note => {
          allNotes.push({
            id: note.id,
            manufacturingId: manufacturingItem.id,
            manufacturingDisplay: `${manufacturingItem.part_name} (${manufacturingItem.lot_number})`,
            noteContent: note.content,
            city: note.city || '',
            createdAt: note.created_at ? note.created_at.split('T')[0] : new Date().toISOString().split('T')[0],
            createdBy: note.created_by || 'System',
            shopName: manufacturingItem.shop?.shop_name || 'No Shop'
          });
        });
      }
    });
    return allNotes;
  }, [manufacturingDataList]);

  // Deleted notes disappear right away (and from the saved copy too)
  const removeNotesLocally = (ids) => {
    setManufacturingDataList(prev => {
      const next = prev.map(item =>
        item.notes ? { ...item, notes: item.notes.filter(n => !ids.includes(n.id)) } : item
      );
      if (shopId) {
        writeJson(`${NOTES_CACHE_PREFIX}${shopId}`, { list: next, shopName: currentShopNameRef.current });
      }
      return next;
    });
  };

  // Data of the shop: show the saved data of the shop immediately, then refresh it.
  useEffect(() => {
    if (prevShopRef.current !== (shopId || null)) {
      prevShopRef.current = shopId || null;
      const cached = shopId ? readJson(`${NOTES_CACHE_PREFIX}${shopId}`) : null;
      const cachedCities = shopId ? readJson(`${NOTES_CITIES_PREFIX}${shopId}`) : null;
      if (cached && Array.isArray(cached.list)) {
        setManufacturingDataList(cached.list);
        setCurrentShopName(cached.shopName || '');
        setIsLoading(false);
      } else {
        setManufacturingDataList([]);
        setCurrentShopName('');
        setIsLoading(true);
      }
      const savedCities = Array.isArray(cachedCities) ? cachedCities : [];
      setHeaderCities(savedCities);
      setShopCities(savedCities);
      setCitiesReady(Array.isArray(cachedCities));
      setHeaderCity('');
      setSelectedNotes([]);
    }

    // shops list is loaded once
    if (!shopsFetchedRef.current) {
      shopsFetchedRef.current = true;
      fetchShops();
    }

    if (shopId) fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shopId]);

  // Cities of the shop (header filter + modal) come from ONE request
  useEffect(() => {
    if (!shopId) {
      setHeaderCities([]);
      setHeaderCity('');
      return;
    }
    const id = shopId;
    const requestId = ++citiesRequestRef.current;

    const fetchHeaderCities = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/shop-cities/?shop_id=${id}`);
        if (requestId !== citiesRequestRef.current) return;
        let citiesData = [];
        if (response.data) {
          if (Array.isArray(response.data.cities)) {
            citiesData = response.data.cities;
          } else if (Array.isArray(response.data)) {
            citiesData = response.data;
          }
        }
        setHeaderCities(citiesData);
        setShopCities(citiesData);
        setCitiesReady(true);
        writeJson(`${NOTES_CITIES_PREFIX}${id}`, citiesData);
        if (citiesData.length === 1) {
          setSelectedCity(citiesData[0]);
        }
      } catch (error) {
        if (requestId !== citiesRequestRef.current) return;
        console.error('Error fetching header cities:', error);
      }
    };

    fetchHeaderCities();
  }, [shopId]);

  useEffect(() => {
    setSelectedNotes([]);
    setShowActionDropdown(false);
  }, [headerCity]);

  const cityCounts = useMemo(() => {
    const counts = {};
    notes.forEach((note) => {
      const city = note.city && note.city.trim() !== '' ? note.city : 'Unknown';
      counts[city] = (counts[city] || 0) + 1;
    });
    return counts;
  }, [notes]);

  const totalNotesCount = notes.length;

  const allCitiesForDropdown = useMemo(() => {
    const set = new Set(headerCities);
    notes.forEach((n) => {
      if (n.city && n.city.trim() !== '') set.add(n.city);
    });
    return Array.from(set).sort();
  }, [headerCities, notes]);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const filteredNotes = notes.filter(note => {
    const cityMatch = !headerCity || (note.city && note.city.toLowerCase() === headerCity.toLowerCase());
    const searchMatch =
      !searchTerm ||
      note.manufacturingDisplay?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      note.noteContent?.toLowerCase().includes(searchTerm.toLowerCase());

    return cityMatch && searchMatch;
  });

  const getCityNoteCount = (city) => {
    if (!city) return notes.length;
    return notes.filter(note =>
      note.city && note.city.toLowerCase() === city.toLowerCase()
    ).length;
  };

  const handleSelectAll = () => {
    if (selectedNotes.length === filteredNotes.length) {
      setSelectedNotes([]);
    } else {
      setSelectedNotes(filteredNotes.map(note => note.id));
    }
    setShowActionDropdown(false);
  };

  const handleSelectNote = (noteId) => {
    if (selectedNotes.includes(noteId)) {
      setSelectedNotes(selectedNotes.filter(id => id !== noteId));
    } else {
      setSelectedNotes([...selectedNotes, noteId]);
    }
    setShowActionDropdown(false);
  };

  const handleDeleteSelected = async () => {
    if (selectedNotes.length === 0) {
      showWarningAlert('Please select notes to delete');
      return;
    }

    const result = await showDeleteConfirmation(null, true, selectedNotes.length);

    if (result.isConfirmed) {
      setSaveLoading(true);
      try {
        let deletedCount = 0;
        const deletedIds = [];
        for (const noteId of selectedNotes) {
          const noteToDelete = notes.find(n => n.id === noteId);
          if (noteToDelete) {
            const response = await axios.delete(
              `${API_BASE_URL}/api/manufacturing/${noteToDelete.manufacturingId}/notes/${noteId}/delete/`
            );
            if (response.status === 200 || response.status === 204) {
              deletedCount++;
              deletedIds.push(noteId);
            }
          }
        }

        removeNotesLocally(deletedIds);
        showSuccessAlert(`${deletedCount} note(s) deleted successfully!`);
        setSelectedNotes([]);
        setShowActionDropdown(false);
        fetchData();
      } catch (error) {
        console.error('Error deleting notes:', error);
        showErrorAlert('Failed to delete notes. Please try again.');
      } finally {
        setSaveLoading(false);
      }
    }
  };

  const handleDeleteSingle = async (note) => {
    const result = await Swal.fire({
      title: 'Delete Note?',
      html: `Are you sure you want to delete this note?<br/><small style="color:#dc3545;">This action cannot be undone.</small>`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#004aad',
      confirmButtonText: 'Yes, Delete',
      cancelButtonText: 'Cancel',
      reverseButtons: true
    });

    if (!result.isConfirmed) return;

    setSaveLoading(true);
    try {
      const response = await axios.delete(
        `${API_BASE_URL}/api/manufacturing/${note.manufacturingId}/notes/${note.id}/delete/`
      );

      if (response.status === 200 || response.status === 204) {
        removeNotesLocally([note.id]);
        showSuccessAlert('Note deleted successfully!');
        setSelectedNotes(selectedNotes.filter(id => id !== note.id));
        fetchData();
      } else {
        showErrorAlert('Failed to delete note');
      }
    } catch (error) {
      console.error('Error deleting note:', error);
      showErrorAlert('Failed to delete note. Please try again.');
    } finally {
      setSaveLoading(false);
    }
  };

  const handleClearAll = () => {
    setSelectedNotes([]);
    setShowActionDropdown(false);
  };

  const handleOpenModalForNew = () => {
    setEditingId(null);
    setSelectedManufacturingId('');
    setNoteContent('');
    setManufacturingSearch('');
    setSelectedCity('');
    if (shopId) {
      fetchCitiesForShop(shopId);
    }
    setIsModalOpen(true);
  };

  const handleOpenModalForEdit = (note) => {
    setEditingId(note.id);
    setSelectedManufacturingId(note.manufacturingId);
    setNoteContent(note.noteContent);
    setSelectedCity(note.city || '');
    setManufacturingSearch('');
    setIsModalOpen(true);
    if (shopId) {
      fetchCitiesForShop(shopId);
    }
  };

  const getUniqueManufacturingItems = () => {
    if (manufacturingDataList.length > 0) {
      return manufacturingDataList.map(item => ({
        id: item.id,
        display: `${item.part_name} (${item.lot_number})`,
        partName: item.part_name,
        lotNumber: item.lot_number,
        shopName: item.shop?.shop_name || 'No Shop',
        city: item.city || ''
      }));
    }
    return [];
  };

  const uniqueManufacturingItems = getUniqueManufacturingItems();

  const filteredManufacturingItems = uniqueManufacturingItems.filter(item =>
    item.display.toLowerCase().includes(manufacturingSearch.toLowerCase()) ||
    item.partName.toLowerCase().includes(manufacturingSearch.toLowerCase()) ||
    item.lotNumber.toLowerCase().includes(manufacturingSearch.toLowerCase())
  );

  const handleSelectManufacturingItem = (item) => {
    setSelectedManufacturingId(item.id);
    setManufacturingSearch('');
    setShowManufacturingDropdown(false);
    if (item.city) {
      setSelectedCity(item.city);
    }
  };

  const getSelectedManufacturingDisplay = () => {
    const selected = uniqueManufacturingItems.find(item => item.id === selectedManufacturingId);
    return selected ? selected.display : '';
  };

  const getSelectedManufacturingShopName = () => {
    const selected = uniqueManufacturingItems.find(item => item.id === selectedManufacturingId);
    return selected ? selected.shopName : '';
  };

  const handleSave = async () => {
    if (!selectedManufacturingId) {
      showWarningAlert('Please select manufacturing data');
      return;
    }

    if (noteContent.trim() === '') {
      showWarningAlert('Please enter note content');
      return;
    }

    if (!selectedCity) {
      showWarningAlert('Please select a city');
      return;
    }

    setSaveLoading(true);

    try {
      const noteData = {
        content: noteContent.trim(),
        city: selectedCity,
        created_by: localStorage.getItem('userName') || 'User'
      };

      if (editingId !== null) {
        const response = await axios.put(
          `${API_BASE_URL}/api/manufacturing/${selectedManufacturingId}/notes/${editingId}/update/`,
          noteData
        );

        if (response.status === 200) {
          showSuccessAlert('Note updated successfully!');
          fetchData();
          handleCloseModal();
        } else {
          showErrorAlert('Failed to update note');
        }
      } else {
        const response = await axios.post(
          `${API_BASE_URL}/api/manufacturing/${selectedManufacturingId}/notes/`,
          noteData
        );

        if (response.status === 201) {
          showSuccessAlert('Note added successfully!');
          fetchData();
          handleCloseModal();
        } else {
          showErrorAlert('Failed to add note');
        }
      }
    } catch (error) {
      console.error('Error saving note:', error);
      showErrorAlert(error.response?.data?.error || 'Failed to save note. Please try again.');
    } finally {
      setSaveLoading(false);
    }
  };

  const handleDelete = async () => {
    if (editingId !== null) {
      const noteToDelete = notes.find(n => n.id === editingId);
      const result = await showDeleteConfirmation(noteToDelete?.noteContent);

      if (result.isConfirmed) {
        setSaveLoading(true);
        try {
          if (noteToDelete) {
            const response = await axios.delete(
              `${API_BASE_URL}/api/manufacturing/${noteToDelete.manufacturingId}/notes/${editingId}/delete/`
            );

            if (response.status === 200 || response.status === 204) {
              removeNotesLocally([editingId]);
              showSuccessAlert('Note deleted successfully!');
              setSelectedNotes(selectedNotes.filter(id => id !== editingId));
              fetchData();
              handleCloseModal();
            } else {
              showErrorAlert('Failed to delete note');
            }
          }
        } catch (error) {
          console.error('Error deleting note:', error);
          showErrorAlert('Failed to delete note. Please try again.');
        } finally {
          setSaveLoading(false);
        }
      }
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedManufacturingId('');
    setNoteContent('');
    setSelectedCity('');
    setEditingId(null);
    setManufacturingSearch('');
    setShowManufacturingDropdown(false);
  };

  const handleClickOutside = (e) => {
    if (showActionDropdown && !e.target.closest('.action-dropdown-container')) {
      setShowActionDropdown(false);
    }
    if (showManufacturingDropdown && !e.target.closest('.manufacturing-dropdown-container')) {
      setShowManufacturingDropdown(false);
    }
    if (showHeaderCityDropdown && !e.target.closest('.notes-city-wrapper')) {
      setShowHeaderCityDropdown(false);
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showActionDropdown, showManufacturingDropdown, showHeaderCityDropdown]);

  const isOperationInProgress = saveLoading;

  return (
    <div className="notes-main-container">
      {/* ===== HEADER ===== */}
      <div className="notes-header-section">
        <div className="notes-header-left">
          <h2 className="notes-header-title">
            <SiNotion className="notes-title-icon" />
            Manufacturing Notes
          </h2>

          {/* ✅ CITY PILL BADGE - Title ke saath hi (pin icon yahan rahega) */}
          <div className="notes-city-pill">
            <FaMapMarkerAlt className="notes-city-pill-icon" />
            <span className="notes-city-pill-text">
              {headerCity
                ? `${headerCity} (${getCityNoteCount(headerCity)})`
                : `All Cities (${notes.length})`}
            </span>
          </div>
        </div>

        <div className="notes-header-right">
          {/* ✅ CUSTOM CITY DROPDOWN (NO PIN ICON) */}
          <div className="notes-city-wrapper">
            <div
              className={`notes-city-trigger ${showHeaderCityDropdown ? 'open' : ''}`}
              onClick={() => {
                if (allCitiesForDropdown.length === 0) return;
                setShowHeaderCityDropdown(!showHeaderCityDropdown);
              }}
            >
              <span className="notes-city-trigger-text">
                {headerCity
                  ? `${headerCity} (${getCityNoteCount(headerCity)})`
                  : `All Cities (${notes.length})`}
              </span>
              <IoMdArrowDropdown className={`notes-city-dropdown-arrow ${showHeaderCityDropdown ? 'rotated' : ''}`} />
            </div>

            {showHeaderCityDropdown && (
              <div className="notes-city-dropdown-menu">
                {/* ALL CITIES option */}
                <div
                  className={`notes-city-option ${headerCity === '' ? 'selected' : ''}`}
                  onClick={() => {
                    setHeaderCity('');
                    setShowHeaderCityDropdown(false);
                  }}
                >
                  <span className="">All Cities</span>
                  <span className="notes-city-option-count">({notes.length})</span>
                </div>

                {/* Individual city options */}
                {allCitiesForDropdown.map((city, index) => (
                  <div
                    key={index}
                    className={`notes-city-option ${headerCity === city ? 'selected' : ''}`}
                    onClick={() => {
                      setHeaderCity(city);
                      setShowHeaderCityDropdown(false);
                    }}
                  >
                    <span className="notes-city-option-name">{city}</span>
                    <span className="notes-city-option-count">({getCityNoteCount(city)})</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="search-wrapper">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search by part name, lot number, or note..."
              value={searchTerm}
              onChange={handleSearch}
              className="search-input"
              disabled={isOperationInProgress}
            />
          </div>
          <button
            className="select-all-btn"
            onClick={handleSelectAll}
            disabled={isOperationInProgress}
          >
            <FaCheckDouble />
            <span>{selectedNotes.length === filteredNotes.length ? 'Deselect All' : 'Select All'}</span>
          </button>
          <button
            className="add-note-btn"
            onClick={handleOpenModalForNew}
            disabled={isOperationInProgress}
          >
            <FaPlus />
            <span>ADD NEW NOTE</span>
          </button>
        </div>
      </div>

      <div className="notes-content">
        {selectedNotes.length > 0 && (
          <div className="selected-summary">
            <div className="summary-content">
              <span className="summary-icon">✓</span>
              <span className="summary-text">
                {selectedNotes.length} note(s) selected
              </span>
              <div className="action-dropdown-container">
                <button
                  className="action-dropdown-btn"
                  onClick={() => setShowActionDropdown(!showActionDropdown)}
                  disabled={isOperationInProgress}
                >
                  <FaEllipsisV />
                  <span>Actions</span>
                </button>
                {showActionDropdown && (
                  <div className="action-dropdown-menu">
                    <button
                      className="dropdown-item delete-item"
                      onClick={handleDeleteSelected}
                      disabled={isOperationInProgress}
                    >
                      <FaTrash />
                      <span>Delete Selected ({selectedNotes.length})</span>
                    </button>
                  </div>
                )}
              </div>
              <button
                className="clear-selection"
                onClick={handleClearAll}
                disabled={isOperationInProgress}
              >
                Clear all
              </button>
            </div>
          </div>
        )}

        <div className="notes-table-container">
          <div className="notes-table-wrapper">
            <table className="notes-table">
              <thead>
                <tr>
                  <th style={{ width: '50px' }}>
                    <input
                      type="checkbox"
                      checked={selectedNotes.length === filteredNotes.length && filteredNotes.length > 0}
                      onChange={handleSelectAll}
                      disabled={isOperationInProgress}
                    />
                  </th>
                  <th>Part Name</th>
                  <th>Note Content</th>
                  <th>City</th>
                  <th>Created At</th>
                  <th style={{ width: '110px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {/* Nothing is shown until the first load is done, so "No notes found" never flashes */}
                {isLoading ? (
                  <tr>
                    <td colSpan="6" style={{ height: '200px' }}></td>
                  </tr>
                ) : (
                  filteredNotes.map((note) => (
                    <tr key={note.id} className={selectedNotes.includes(note.id) ? 'selected' : ''}>
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedNotes.includes(note.id)}
                          onChange={() => handleSelectNote(note.id)}
                          disabled={isOperationInProgress}
                        />
                      </td>
                      <td>{note.manufacturingDisplay}</td>
                      <td>{note.noteContent}</td>
                      <td>{note.city ? note.city : '-'}</td>
                      <td>{note.createdAt}</td>
                      <td className="actions-cell">
                        <button
                          className="edit-note-btn"
                          onClick={() => handleOpenModalForEdit(note)}
                          title="Edit Note"
                          disabled={isOperationInProgress}
                        >
                          <FaEdit />
                        </button>
                        <button
                          className="delete-note-btn"
                          onClick={() => handleDeleteSingle(note)}
                          title="Delete Note"
                          disabled={isOperationInProgress}
                        >
                          <RiDeleteBin5Fill />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {!isLoading && filteredNotes.length === 0 && (
            <div className="no-notes">
              <FaStickyNote className="no-data-icon" />
              <p>
                {headerCity
                  ? `No notes found for city "${headerCity}" in ${currentShopName || `Shop ${shopId}`}.`
                  : `No notes found for ${currentShopName || `Shop ${shopId}`}.`}
              </p>
              <button
                className="add-first-btn"
                onClick={handleOpenModalForNew}
                disabled={isOperationInProgress}
              >
                <FaPlus /> Add Your First Note
              </button>
            </div>
          )}
        </div>
      </div>

      {isModalOpen && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingId !== null ? 'Edit Manufacturing Note' : 'Add Manufacturing Note'}</h3>
              <button className="modal-close" onClick={handleCloseModal}>×</button>
            </div>
            <div className="modal-body">
              <form className="note-form" onSubmit={(e) => e.preventDefault()}>

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'flex-start' }}>

                  <div
                    className="form-group manufacturing-dropdown-container"
                    style={{ flex: '1 1 240px', minWidth: 0 }}
                  >
                    <label className="form-label-with-icon">
                      SELECT MANUFACTURING DATA:
                    </label>
                    <div className="dropdown-wrapper">
                      <div
                        className="dropdown-input"
                        onClick={() => !isOperationInProgress && setShowManufacturingDropdown(!showManufacturingDropdown)}
                      >
                        <input
                          type="text"
                          value={getSelectedManufacturingDisplay()}
                          placeholder={manufacturingDataList.length === 0 ? "No manufacturing data available" : "Select or search part name / lot number..."}
                          className="manufacturing-input"
                          readOnly
                          disabled={isOperationInProgress || manufacturingDataList.length === 0}
                        />
                        <FaChevronDown className={`dropdown-icon ${showManufacturingDropdown ? 'rotated' : ''}`} />
                      </div>
                      {showManufacturingDropdown && manufacturingDataList.length > 0 && (
                        <div className="manufacturing-dropdown">
                          <div className="dropdown-list">
                            {filteredManufacturingItems.length > 0 ? (
                              filteredManufacturingItems.map((item) => (
                                <div
                                  key={item.id}
                                  className={`dropdown-item ${selectedManufacturingId === item.id ? 'selected' : ''}`}
                                  onClick={() => handleSelectManufacturingItem(item)}
                                >
                                  <FaIndustry className="dropdown-item-icon" />
                                  <div className="dropdown-item-content">
                                    <span className="item-part-name">{item.partName}</span>
                                    <span className="item-lot-number">({item.lotNumber})</span>
                                    <span className="item-shop-name"> - {item.shopName}</span>
                                  </div>
                                </div>
                              ))
                            ) : (
                              <div className="dropdown-no-data">
                                No matching manufacturing data found.
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                    {!isLoading && manufacturingDataList.length === 0 && (
                      <small className="form-help-text">
                        No manufacturing data available for {currentShopName}. Please add manufacturing data first.
                      </small>
                    )}
                  </div>

                  <div
                    className="form-group"
                    style={{ flex: '1 1 240px', minWidth: 0 }}
                  >
                    <label className="form-label-with-icon">
                      SELECT CITY:
                    </label>
                    <div className="city-select-wrapper">
                      <select
                        value={selectedCity}
                        onChange={(e) => setSelectedCity(e.target.value)}
                        className="city-dropdown"
                        disabled={shopCities.length === 0}
                      >
                        <option value="">-- Select a city --</option>
                        {shopCities.map((city, index) => (
                          <option key={index} value={city}>
                            {city}
                          </option>
                        ))}
                      </select>
                      {citiesReady && shopCities.length === 0 && (
                        <div className="city-empty-text">No cities available for this shop</div>
                      )}
                    </div>
                  </div>

                </div>

                <div className="form-group">
                  <label className="form-label-with-icon">NOTE CONTENT *</label>
                  <textarea
                    name="noteContent"
                    value={noteContent}
                    onChange={(e) => setNoteContent(e.target.value)}
                    placeholder="Enter note content..."
                    rows="6"
                    className="note-textarea"
                    disabled={isOperationInProgress}
                  />
                </div>

                {editingId !== null && (
                  <div className="form-group">
                    <label>CREATED AT</label>
                    <input
                      type="text"
                      value={notes.find(n => n.id === editingId)?.createdAt || ''}
                      disabled
                      className="disabled-field"
                    />
                  </div>
                )}
              </form>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={handleCloseModal} disabled={isOperationInProgress}>
                Cancel
              </button>
              {editingId !== null && (
                <button className="btn-delete" onClick={handleDelete} disabled={isOperationInProgress}>
                  Delete
                </button>
              )}
              <button className="btn-save" onClick={handleSave} disabled={isOperationInProgress}>
                {isOperationInProgress ? 'Saving...' : (editingId !== null ? 'Update Note' : 'Save Note')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Notes;